import { keys, values } from 'lodash'

import { MATCH_COL_INDEX, MATCH_ROW_INDEX } from 'modules/payroll_bundle/lib/constants'

export type WorksheetData = Array<Array<string | number>>

export interface SEPAMatchDataType {
  name?: string
  last_name?: string
  amount?: number
  IBAN: string | null
  userId: number | null

  [key: string]: string | number | null | undefined
}

export interface ResultMatchDataType {
  name?: string | null
  last_name?: string | null
  net_salary?: number
  base_salary?: number
  gross_salary?: number
  company_costs?: number
  userId: number | null
  identifier?: string
  company_identifier?: string
  employee_id?: number
  payroll_summary_employee_id?: string

  [key: string]: string | number | null | undefined
}

export interface SupplementMatchDataType {
  employee_id: number | null
  userId: number | null
  name?: string
  last_name?: string
  identifier?: string
  amount?: number | string | null | undefined

  [key: string]: string | number | null | undefined
}

export type MatchDataType = ResultMatchDataType | SupplementMatchDataType

export type SupplementType = {
  id: string
  legalEntityIdToTaxonomyId: Record<number, number>
  label: string
  units: string
  mappingsCount: number
}

type RowIndexType = number
type CellIndexType = number
type CellName = string | number

export type MatchElement = [RowIndexType, CellIndexType, CellName]

interface NameProps {
  name?: MatchElement
  last_name?: MatchElement
}

export type ResultMatches = NameProps & {
  [key: string]: MatchElement
}

// Although matchElement is defined as an array of 2 numbers this type allows
//     { last_name: [1, 1, 'Doe'], 1669142039980: [1, 4, 200], ... }
// See WorksheetTable.tsx setCellMatch()
export type SupplementsMatches = {
  [key: string]: MatchElement
}

interface Props {
  worksheetData: WorksheetData
  worksheetMatches: ResultMatches | SupplementsMatches
  defaultMatchedData: MatchDataType
}

export const matchData = ({ worksheetData, worksheetMatches, defaultMatchedData }: Props) => {
  const matches: MatchElement[] = values(worksheetMatches)
  const matchedDataArray: Array<MatchDataType> = []
  const firstMatch = matches[0]

  if (
    matches.every(
      (m: MatchElement) =>
        firstMatch !== undefined && m[MATCH_ROW_INDEX] === firstMatch[MATCH_ROW_INDEX]
    )
  ) {
    // employee data is located in each row
    matchDataByRow(matches, worksheetData, worksheetMatches, matchedDataArray, defaultMatchedData)
  } else if (
    matches.every(
      (m: MatchElement) =>
        firstMatch !== undefined && m[MATCH_COL_INDEX] === firstMatch[MATCH_COL_INDEX]
    )
  ) {
    // employee data is located in each column
    matchDataByColumn(
      matches,
      worksheetData,
      worksheetMatches,
      matchedDataArray,
      defaultMatchedData
    )
  }

  return matchedDataArray
}

const matchDataByColumn = <T extends MatchDataType>(
  matches: MatchElement[],
  worksheetData: WorksheetData,
  worksheetMatches: ResultMatches | SupplementsMatches,
  matchedDataArray: Array<T>,
  defaultMatchedData: T
) => {
  const firstColIndex = Number(matches[0]?.[MATCH_COL_INDEX])
  const length = worksheetData?.[0]?.length ?? 0

  for (let colIndex = firstColIndex; colIndex < length; colIndex++) {
    const matchedData: T = {
      ...defaultMatchedData,
    }
    keys(worksheetMatches).forEach((key: string) => {
      const rowIndex = Number(worksheetMatches?.[key]?.[MATCH_ROW_INDEX])
      if (key === 'net_salary' && worksheetData?.[rowIndex]?.[colIndex] === null) {
        if (
          worksheetData &&
          worksheetData[rowIndex] &&
          worksheetData[rowIndex]![colIndex] !== undefined
        ) {
          worksheetData[rowIndex]![colIndex] = 0
        }
      }
      matchedData[key] = worksheetData?.[rowIndex]?.[colIndex] ?? null
    })
    matchedDataArray.push(matchedData)
  }
}

const matchDataByRow = <T extends MatchDataType>(
  matches: MatchElement[],
  worksheetData: WorksheetData,
  worksheetMatches: ResultMatches | SupplementsMatches,
  matchedDataArray: Array<T>,
  defaultMatchedData: T
) => {
  const firstRowIndex = Number(matches[0]?.[MATCH_ROW_INDEX])

  for (let rowIndex = firstRowIndex; rowIndex < worksheetData.length; rowIndex++) {
    const matchedData: T = {
      ...defaultMatchedData,
    }
    keys(worksheetMatches).forEach((key: string) => {
      const colIndex = Number(worksheetMatches?.[key]?.[MATCH_COL_INDEX])
      if (key === 'net_salary' && worksheetData?.[rowIndex]?.[colIndex] === null) {
        if (
          worksheetData &&
          worksheetData[rowIndex] &&
          worksheetData[rowIndex]![colIndex] !== undefined
        ) {
          worksheetData[rowIndex]![colIndex] = 0
        }
      }
      matchedData[key] = worksheetData?.[rowIndex]?.[colIndex]
    })
    matchedDataArray.push(matchedData)
  }
}

export interface VisibleButtons {
  [key: string]: string | boolean
}
